import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Phone } from 'lucide-react';

const STORAGE_KEY = 'fundle_whatsapp_popup_dismissed_v1';

const WhatsAppPopup = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'true') {
        setDismissed(true);
        return;
      }
    } catch (e) {
      // ignore
    }
    const timer = setTimeout(() => setShow(true), 15000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // ignore
    }
  };

  // Keep internal consoles clean; the CTA shows on every public page.
  if (pathname.startsWith('/admin') || pathname.startsWith('/leads')) return null;
  if (!show || dismissed) return null;

  return (
    <div className="fixed bottom-24 right-6 z-[9999] animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="whatsapp-popup">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 w-[320px] relative">
        <button
          onClick={handleDismiss}
          aria-label="Close"
          className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-slate-900 hover:bg-slate-700 text-white shadow-lg flex items-center justify-center z-10"
          data-testid="whatsapp-popup-close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Phone size={18} className="text-[#0B1437]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#0B1437] leading-tight">Talk to our loyalty experts</h3>
            <p className="text-[#4A5878] text-xs mt-0.5">Replies within minutes</p>
          </div>
        </div>
        <div className="space-y-2">
          <a href="https://wa.me/919910530372?text=Hi%20Abhinav!%20I%27d%20like%20to%20learn%20about%20Fundle%27s%20AI%20loyalty%20platform." target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors group cursor-pointer w-full no-underline" data-testid="popup-whatsapp-abhinav">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.634-1.215A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.09 0-4.037-.654-5.639-1.77l-.405-.265-2.749.721.734-2.682-.29-.42A9.708 9.708 0 012.25 12 9.75 9.75 0 0112 2.25 9.75 9.75 0 0121.75 12 9.75 9.75 0 0112 21.75z"/></svg>
            </div>
            <div className="min-w-0">
              <p className="text-emerald-800 font-bold text-xs">Chat with Abhinav</p>
              <p className="text-[#0B1437] text-[11px]">+91-99105 30372</p>
            </div>
          </a>
          <a href="https://wa.me/919899533604?text=Hi%20Anmol!%20I%27d%20like%20to%20learn%20about%20Fundle%27s%20AI%20loyalty%20platform." target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors group cursor-pointer w-full no-underline" data-testid="popup-whatsapp-anmol">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.634-1.215A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.09 0-4.037-.654-5.639-1.77l-.405-.265-2.749.721.734-2.682-.29-.42A9.708 9.708 0 012.25 12 9.75 9.75 0 0112 2.25 9.75 9.75 0 0121.75 12 9.75 9.75 0 0112 21.75z"/></svg>
            </div>
            <div className="min-w-0">
              <p className="text-emerald-800 font-bold text-xs">Chat with Anmol</p>
              <p className="text-[#0B1437] text-[11px]">+91-98995 33604</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppPopup;
